import React, { useState, useEffect, useMemo, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl } from "../../../Basurl/Baseurl";
import { fetchGuestHouses, fetchGuestHouseById } from "../../../services/guestHouseApi";
import {
  createGHServiceRecord,
  GH_PAYMENT_METHODS,
  GH_SERVICE_DURATIONS,
} from "../../../services/guestHouseServicesApi";

const COMMON_PRESETS = [
  { name: "Laundry Service", price: 500, duration: "One-Time" },
  { name: "Special Nursing Care", price: 1200, duration: "Day" },
  { name: "Airport Drop / Pickup", price: 1500, duration: "One-Time" },
  { name: "Hospital Shuttle", price: 600, duration: "One-Time" },
  { name: "Special Meal Plan (3x Daily)", price: 800, duration: "Day" },
  { name: "Wheelchair Rental", price: 300, duration: "Day" },
  { name: "Extra Bed & Linen", price: 500, duration: "Day" },
  { name: "Oxygen Concentrator Rental", price: 1000, duration: "Day" },
  { name: "Attendant Bed", price: 400, duration: "Day" },
  { name: "High-Speed Wi-Fi Router", price: 200, duration: "Day" },
];

export default function CreateGHServiceModal({
  open,
  onClose,
  prefilledPatient = null,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  // Patient selection states
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientSearch, setPatientSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Guest houses and rooms catalog
  const [guestHousesCatalog, setGuestHousesCatalog] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    treatment_id: "",
    guestHouseName: "",
    roomNumber: "",
    stayDateRangeFrom: new Date().toISOString().slice(0, 10),
    stayDateRangeTo: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    notes: "",
    saveServicesToMaster: true,
  });

  // Services rows
  const [services, setServices] = useState([
    {
      serviceId: "",
      serviceName: "",
      description: "",
      price: "",
      quantity: 1,
      duration: "One-Time",
      discount_percentage: 0,
    },
  ]);

  // Initial payment
  const [hasInitialPayment, setHasInitialPayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paid_amount: "",
    paymentMethod: "UPI",
    payment_Date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  // Load patients and guest houses on open
  useEffect(() => {
    if (open) {
      loadPatients();
      loadGuestHouses();

      if (prefilledPatient) {
        setSelectedPatient(prefilledPatient);
        setFormData((prev) => ({
          ...prev,
          treatment_id: prefilledPatient.treatment_id || prefilledPatient.treatmentId || "",
        }));
      }
    }
  }, [open, prefilledPatient]);

  // Dynamic Room Fetching when guest house changes
  useEffect(() => {
    if (!formData.guestHouseName) {
      setAvailableRooms([]);
      return;
    }
    const matchedGH = guestHousesCatalog.find(
      (gh) =>
        String(gh.name || gh.title || "").trim().toLowerCase() ===
        formData.guestHouseName.trim().toLowerCase()
    );
    if (matchedGH && matchedGH.id) {
      setLoadingRooms(true);
      fetchGuestHouseById(matchedGH.id)
        .then((res) => {
          const rooms = res?.data?.rooms || res?.rooms || [];
          setAvailableRooms(Array.isArray(rooms) ? rooms : []);
        })
        .catch((err) => {
          console.warn("Could not fetch rooms for guest house:", err);
          setAvailableRooms([]);
        })
        .finally(() => setLoadingRooms(false));
    }
  }, [formData.guestHouseName, guestHousesCatalog]);

  // Click outside to close patient dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [dropdownOpen]);

  const loadPatients = async () => {
    setLoadingPatients(true);
    try {
      const token = localStorage.getItem("token") || "";
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const firstRes = await axios.get(
        `${baseurl}all_patients?page=1&limit=3000&search=`,
        { headers, timeout: 20000 }
      );
      const firstData = firstRes.data;
      const allPatients = [
        ...(firstData?.details ||
          firstData?.data?.details ||
          firstData?.patients ||
          firstData?.data?.patients ||
          (Array.isArray(firstData?.data) ? firstData.data : []) ||
          (Array.isArray(firstData) ? firstData : []) ||
          []),
      ];

      // Handle pagination if backend caps limit
      const totalPages = Number(firstData?.pagination?.totalPages || 1);
      if (totalPages > 1) {
        const pagePromises = [];
        for (let p = 2; p <= totalPages; p++) {
          pagePromises.push(
            axios
              .get(`${baseurl}all_patients?page=${p}&limit=500&search=`, {
                headers,
                timeout: 15000,
              })
              .then((res) => {
                const pData = res.data;
                return (
                  pData?.details ||
                  pData?.data?.details ||
                  pData?.patients ||
                  pData?.data?.patients ||
                  (Array.isArray(pData?.data) ? pData.data : []) ||
                  []
                );
              })
              .catch(() => [])
          );
        }
        const remainingPages = await Promise.all(pagePromises);
        remainingPages.forEach((pList) => {
          if (Array.isArray(pList)) {
            allPatients.push(...pList);
          }
        });
      }

      if (Array.isArray(allPatients)) {
        const seenIds = new Set();
        const cleaned = [];

        allPatients.forEach((p, idx) => {
          const id = p.patientId || p.patient_id || p._id || p.id || `P-${idx}`;
          const key = String(p._id || id);
          if (seenIds.has(key)) return;
          seenIds.add(key);

          const name = p.patient_name || p.name || p.patientName || "Unknown Patient";
          const phone = p.phone || p.phone_number || p.mobile || p.contact_number || "";
          const country = p.country || p.nationality || "";
          const treatment_id = p.treatment_id || p.treatmentId || "";
          const email = p.email || p.email_id || "";

          cleaned.push({
            _id: p._id || id,
            patientId: id,
            name,
            phone,
            country,
            treatment_id,
            email,
            raw: p,
          });
        });

        setPatients(cleaned);
      }
    } catch (err) {
      console.warn("Could not load patients list:", err);
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadGuestHouses = async () => {
    try {
      const res = await fetchGuestHouses();
      const list = Array.isArray(res) ? res : res?.data || res?.details || [];
      setGuestHousesCatalog(list);
    } catch (e) {
      console.warn("Could not load guest houses catalog:", e);
    }
  };

  // Dynamic search across all fields without cutting off list
  const filteredPatients = useMemo(() => {
    const q = patientSearch.toLowerCase().trim();
    if (!q) return patients;
    const cleanDigits = q.replace(/\D/g, "");
    return patients.filter((p) => {
      const matchName = String(p.name || "").toLowerCase().includes(q);
      const matchId = String(p.patientId || "").toLowerCase().includes(q);
      const matchTreatment = String(p.treatment_id || "").toLowerCase().includes(q);
      const matchCountry = String(p.country || "").toLowerCase().includes(q);
      const matchEmail = String(p.email || "").toLowerCase().includes(q);
      const phoneStr = String(p.phone || "");
      const matchPhone =
        phoneStr.toLowerCase().includes(q) ||
        (cleanDigits.length >= 3 && phoneStr.replace(/\D/g, "").includes(cleanDigits));
      return matchName || matchId || matchPhone || matchTreatment || matchCountry || matchEmail;
    });
  }, [patients, patientSearch]);

  const handleSelectPatient = (p) => {
    setSelectedPatient(p);
    setDropdownOpen(false);
    setPatientSearch("");
    if (p.treatment_id) {
      setFormData((prev) => ({ ...prev, treatment_id: p.treatment_id }));
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleServiceChange = (index, field, value) => {
    setServices((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      if (field === "serviceName") {
        const found = COMMON_PRESETS.find(
          (p) => p.name.toLowerCase() === String(value || "").trim().toLowerCase()
        );
        if (found && (!updated.price || updated.price === "")) {
          updated.price = found.price;
          updated.duration = found.duration;
        }
      }
      next[index] = updated;
      return next;
    });
  };

  const handleSelectPreset = (index, preset) => {
    setServices((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        serviceName: preset.name,
        price: preset.price,
        duration: preset.duration,
      };
      return next;
    });
  };

  const addServiceRow = () => {
    setServices((prev) => [
      ...prev,
      {
        serviceId: "",
        serviceName: "",
        description: "",
        price: "",
        quantity: 1,
        duration: "One-Time",
        discount_percentage: 0,
      },
    ]);
  };

  const removeServiceRow = (index) => {
    if (services.length === 1) return;
    setServices((prev) => prev.filter((_, i) => i !== index));
  };

  // Computations
  const totalBilling = services.reduce((sum, s) => {
    const p = Number(s.price) || 0;
    const q = Number(s.quantity) || 1;
    const d = Number(s.discount_percentage) || 0;
    const base = p * q;
    return sum + (d ? base - (base * d) / 100 : base);
  }, 0);

  const initialPaid = hasInitialPayment ? Number(paymentData.paid_amount) || 0 : 0;
  const netDue = Math.max(0, totalBilling - initialPaid);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loadingRef.current) return;

    if (!selectedPatient) {
      Swal.fire("Required", "Please select a patient.", "warning");
      return;
    }
    if (!formData.guestHouseName.trim()) {
      Swal.fire("Required", "Please enter or select a Guest House name.", "warning");
      return;
    }

    const validServices = services.filter((s) => s.serviceName.trim() && Number(s.price) >= 0);
    if (validServices.length === 0) {
      Swal.fire("Required", "Please add at least one valid service with a name and price.", "warning");
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      const payload = {
        patientId: selectedPatient.patientId || selectedPatient._id,
        patient_name: selectedPatient.name,
        treatment_id: formData.treatment_id.trim() || undefined,
        guestHouseName: formData.guestHouseName.trim(),
        roomNumber: formData.roomNumber.trim(),
        stayDateRangeFrom: formData.stayDateRangeFrom,
        stayDateRangeTo: formData.stayDateRangeTo,
        notes: formData.notes.trim(),
        saveServicesToMaster: formData.saveServicesToMaster,
        services: validServices.map((s) => ({
          serviceId: s.serviceId || undefined,
          serviceName: s.serviceName.trim(),
          description: s.description?.trim() || undefined,
          price: Number(s.price),
          quantity: Number(s.quantity) || 1,
          duration: s.duration,
          discount_percentage: Number(s.discount_percentage) || 0,
        })),
        initialPayment: hasInitialPayment && initialPaid > 0
          ? {
              paid_amount: initialPaid,
              paymentMethod: paymentData.paymentMethod,
              payment_Date: paymentData.payment_Date,
              notes: paymentData.notes.trim(),
            }
          : undefined,
      };

      const res = await createGHServiceRecord(payload);
      Swal.fire({
        title: "Record Created!",
        text: res?.message || "Guest house service record created successfully.",
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error creating guest house service record:", err);
      Swal.fire("Error", typeof err === "string" ? err : "Failed to create record", "error");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="gh-modal-dialog gh-modal-md"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "calc(100vh - 48px)",
          height: "min(860px, calc(100vh - 48px))",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
        },
      }}
    >
      {/* Pinned Sticky Header */}
      <div
        className="gh-modal-header"
        style={{
          background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
          padding: "16px 22px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="fa fa-building fs-5" />
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ fontSize: "17px", color: "#fff" }}>
              New Guest House Service Booking
            </h5>
            <small style={{ color: "rgba(255,255,255,0.85)" }}>
              Book guest house stay, room, and services for a patient
            </small>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <DialogContent
          style={{
            padding: "20px 22px",
            overflowY: "auto",
            flex: "1 1 auto",
          }}
        >
          {/* Section 1: Patient Selection */}
          <div className="card border p-3 mb-3 bg-light">
            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
              <i className="fa fa-user text-primary" /> 1. Patient Information
            </h6>
            <div className="row g-2 align-items-end">
              <div className="col-md-7 position-relative" ref={dropdownRef}>
                <label className="form-label small fw-bold text-dark mb-1 d-flex justify-content-between">
                  <span>
                    Select Patient <span className="text-danger">*</span>
                  </span>
                  {patients.length > 0 && (
                    <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>
                      {patients.length} patients loaded
                    </span>
                  )}
                </label>

                {selectedPatient ? (
                  <div
                    className="p-2 border rounded bg-white d-flex justify-content-between align-items-center shadow-sm"
                    style={{ minHeight: "42px", borderColor: "#bae6fd" }}
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <strong className="text-primary" style={{ fontSize: "14px" }}>
                        {selectedPatient.name}
                      </strong>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "#e0f2fe",
                          color: "#0369a1",
                          border: "1px solid #bae6fd",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {selectedPatient.patientId}
                      </span>
                      {selectedPatient.treatment_id && (
                        <span className="badge bg-light text-muted border" style={{ fontSize: "11px" }}>
                          TRT: {selectedPatient.treatment_id}
                        </span>
                      )}
                      {selectedPatient.phone && (
                        <span className="small text-muted">
                          <i className="fa fa-phone me-1" style={{ fontSize: "10px" }} />
                          {selectedPatient.phone}
                        </span>
                      )}
                      {selectedPatient.country && (
                        <span className="badge bg-light text-dark border" style={{ fontSize: "10.5px" }}>
                          <i className="fa fa-globe me-1 text-muted" style={{ fontSize: "10px" }} />
                          {selectedPatient.country}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger py-0 px-2 ms-2"
                      style={{ fontSize: "12px", borderRadius: "6px" }}
                      onClick={() => {
                        setSelectedPatient(null);
                        setPatientSearch("");
                      }}
                      title="Clear Selection"
                    >
                      <i className="fa fa-times me-1" /> Change
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa fa-search text-muted" />
                      </span>
                      <input
                        type="text"
                        placeholder={
                          loadingPatients
                            ? "Loading patient database..."
                            : `Search patient by name, ID, phone, country... (${patients.length} available)`
                        }
                        value={patientSearch}
                        onFocus={() => setDropdownOpen(true)}
                        onChange={(e) => {
                          setPatientSearch(e.target.value);
                          setDropdownOpen(true);
                        }}
                        className="form-control"
                      />
                      {patientSearch && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary bg-white border-start-0"
                          onClick={() => setPatientSearch("")}
                          title="Clear search"
                        >
                          <i className="fa fa-times text-muted" />
                        </button>
                      )}
                    </div>

                    {dropdownOpen && (
                      <div
                        className="dropdown-menu show w-100 mt-1 shadow-lg"
                        style={{
                          maxHeight: "340px",
                          overflowY: "auto",
                          zIndex: 1050,
                          borderRadius: "8px",
                          border: "1px solid #cbd5e1",
                          padding: 0,
                          boxShadow: "0 12px 28px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        {/* Header bar showing counts */}
                        <div
                          className="px-3 py-2 bg-light border-bottom d-flex justify-content-between align-items-center text-muted sticky-top"
                          style={{ fontSize: "11.5px" }}
                        >
                          <span>
                            {loadingPatients ? (
                              <span>
                                <i className="fa fa-spinner fa-spin me-1 text-primary" /> Loading all patients...
                              </span>
                            ) : (
                              <span>
                                Showing <strong>{Math.min(filteredPatients.length, 300)}</strong> of{" "}
                                <strong>{filteredPatients.length}</strong> patients{" "}
                                {patients.length > 0 && <span className="text-muted">({patients.length} total)</span>}
                              </span>
                            )}
                          </span>
                          {patientSearch && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-decoration-none"
                              style={{ fontSize: "11px" }}
                              onClick={() => setPatientSearch("")}
                            >
                              Show All
                            </button>
                          )}
                        </div>

                        {loadingPatients ? (
                          <div className="p-4 text-center text-muted small">
                            <i className="fa fa-spinner fa-spin me-2 text-primary fs-5 d-block mb-2" />
                            Loading patient database...
                          </div>
                        ) : filteredPatients.length === 0 ? (
                          <div className="p-4 text-center text-muted small">
                            <i className="fa fa-info-circle me-1" /> No patients found matching "{patientSearch}"
                          </div>
                        ) : (
                          <>
                            {filteredPatients.slice(0, 300).map((p, idx) => {
                              const isSelected =
                                selectedPatient &&
                                (selectedPatient.patientId === p.patientId || selectedPatient._id === p._id);
                              return (
                                <button
                                  key={p._id || p.patientId || idx}
                                  type="button"
                                  className="dropdown-item py-2 px-3 border-bottom d-flex justify-content-between align-items-center"
                                  style={{
                                    backgroundColor: isSelected ? "#e0f2fe" : "transparent",
                                    transition: "background 0.15s ease",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleSelectPatient(p)}
                                >
                                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      <span className="fw-semibold text-dark" style={{ fontSize: "13.5px" }}>
                                        {p.name}
                                      </span>
                                      {p.patientId && (
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#e0f2fe",
                                            color: "#0369a1",
                                            border: "1px solid #bae6fd",
                                            fontSize: "11px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {p.patientId}
                                        </span>
                                      )}
                                      {p.treatment_id && (
                                        <span className="badge bg-light text-muted border" style={{ fontSize: "10.5px" }}>
                                          TRT: {p.treatment_id}
                                        </span>
                                      )}
                                    </div>
                                    <div className="d-flex align-items-center gap-3 mt-1 text-muted" style={{ fontSize: "11.5px" }}>
                                      {p.phone && (
                                        <span>
                                          <i className="fa fa-phone me-1" style={{ fontSize: "10px" }} />
                                          {p.phone}
                                        </span>
                                      )}
                                      {p.country && (
                                        <span>
                                          <i className="fa fa-globe me-1" style={{ fontSize: "10px" }} />
                                          {p.country}
                                        </span>
                                      )}
                                      {p.email && (
                                        <span>
                                          <i className="fa fa-envelope me-1" style={{ fontSize: "10px" }} />
                                          {p.email}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className={`badge ${isSelected ? "bg-primary text-white" : "bg-light text-primary border"} ms-2`}>
                                    {isSelected ? "Selected" : "Select"}
                                  </span>
                                </button>
                              );
                            })}
                            {filteredPatients.length > 300 && (
                              <div className="p-2 text-center text-muted bg-light border-top" style={{ fontSize: "11px" }}>
                                Showing first 300 of {filteredPatients.length} matches. Type in the box to narrow down.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="col-md-5">
                <label className="form-label small fw-bold text-dark mb-1">
                  Treatment / Case ID (Optional)
                </label>
                <input
                  type="text"
                  name="treatment_id"
                  placeholder="e.g. TRT-0042"
                  value={formData.treatment_id}
                  onChange={handleFormChange}
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Stay Information */}
          <div className="card border p-3 mb-3 bg-light">
            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
              <i className="fa fa-bed text-info" /> 2. Guest House & Stay Details
            </h6>
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label small fw-bold text-dark mb-1 d-flex justify-content-between">
                  <span>
                    Guest House Name <span className="text-danger">*</span>
                  </span>
                  {guestHousesCatalog.length > 0 && (
                    <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>
                      {guestHousesCatalog.length} properties
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  required
                  list="guesthouses-list"
                  name="guestHouseName"
                  placeholder="Select or type guest house name"
                  value={formData.guestHouseName}
                  onChange={handleFormChange}
                  className="form-control"
                />
                <datalist id="guesthouses-list">
                  {guestHousesCatalog.map((gh, idx) => (
                    <option key={gh.id || idx} value={gh.name || gh.title}>
                      {gh.address ? `${gh.address} ` : ""}{gh.rooms?.length ? `(${gh.rooms.length} rooms)` : ""}
                    </option>
                  ))}
                </datalist>
              </div>

              <div className="col-md-3">
                <label className="form-label small fw-bold text-dark mb-1 d-flex justify-content-between">
                  <span>Room Number</span>
                  {loadingRooms && (
                    <span className="text-primary" style={{ fontSize: "10.5px" }}>
                      <i className="fa fa-spinner fa-spin me-1" /> Loading
                    </span>
                  )}
                  {!loadingRooms && availableRooms.length > 0 && (
                    <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>
                      {availableRooms.length} rooms
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  list="rooms-list"
                  name="roomNumber"
                  placeholder={
                    loadingRooms
                      ? "Loading rooms..."
                      : availableRooms.length > 0
                      ? "Select or type room"
                      : "e.g. 204, Suite 3"
                  }
                  value={formData.roomNumber}
                  onChange={handleFormChange}
                  className="form-control"
                />
                <datalist id="rooms-list">
                  {availableRooms.map((rm, idx) => (
                    <option key={rm.id || idx} value={rm.title || rm.room_number || rm.name}>
                      {rm.price ? `₹${rm.price} ` : ""}{rm.availability !== undefined ? `(Avail: ${rm.availability})` : ""}
                    </option>
                  ))}
                </datalist>
              </div>

              <div className="col-md-2">
                <label className="form-label small fw-bold text-dark mb-1">Check-in From</label>
                <input
                  type="date"
                  name="stayDateRangeFrom"
                  value={formData.stayDateRangeFrom}
                  onChange={handleFormChange}
                  className="form-control"
                />
              </div>

              <div className="col-md-2">
                <label className="form-label small fw-bold text-dark mb-1">Check-out To</label>
                <input
                  type="date"
                  name="stayDateRangeTo"
                  value={formData.stayDateRangeTo}
                  onChange={handleFormChange}
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Services Dynamic Rows */}
          <div className="card border p-3 mb-3 bg-light">
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                <i className="fa fa-list-check text-success" /> 3. Services & Amenities Requested
              </h6>
              <div className="d-flex align-items-center gap-1">
                {COMMON_PRESETS.slice(0, 3).map((p, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                    style={{ fontSize: "11px" }}
                    onClick={() => {
                      setServices((prev) => [
                        ...prev,
                        {
                          serviceId: "",
                          serviceName: p.name,
                          description: "",
                          price: p.price,
                          quantity: 1,
                          duration: p.duration,
                          discount_percentage: 0,
                        },
                      ]);
                    }}
                  >
                    + {p.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="d-flex flex-column gap-2">
              {services.map((s, index) => {
                const base = (Number(s.price) || 0) * (Number(s.quantity) || 1);
                const disc = (base * (Number(s.discount_percentage) || 0)) / 100;
                const total = Math.max(0, base - disc);

                return (
                  <div key={index} className="p-3 border rounded bg-white shadow-sm">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="badge bg-light text-dark border">
                        Service #{index + 1}
                      </span>
                      {services.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm py-0 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => removeServiceRow(index)}
                        >
                          <i className="fa fa-trash me-1" /> Remove
                        </button>
                      )}
                    </div>

                    <div className="row g-2 align-items-center">
                      <div className="col-md-4">
                        <label className="form-label small text-muted mb-0">Service Name *</label>
                        <input
                          type="text"
                          required
                          list="services-preset-list"
                          placeholder="e.g. Laundry, Special Nursing"
                          value={s.serviceName}
                          onChange={(e) => handleServiceChange(index, "serviceName", e.target.value)}
                          className="form-control form-control-sm"
                        />
                        <datalist id="services-preset-list">
                          {COMMON_PRESETS.map((p, pIdx) => (
                            <option key={pIdx} value={p.name}>
                              ₹{p.price} / {p.duration}
                            </option>
                          ))}
                        </datalist>
                      </div>

                      <div className="col-md-2">
                        <label className="form-label small text-muted mb-0">Duration</label>
                        <select
                          value={s.duration}
                          onChange={(e) => handleServiceChange(index, "duration", e.target.value)}
                          className="form-select form-select-sm"
                        >
                          {GH_SERVICE_DURATIONS.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-2">
                        <label className="form-label small text-muted mb-0">Unit Price (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          required
                          placeholder="Price"
                          value={s.price}
                          onChange={(e) => handleServiceChange(index, "price", e.target.value)}
                          className="form-control form-control-sm"
                        />
                      </div>

                      <div className="col-md-1">
                        <label className="form-label small text-muted mb-0">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={s.quantity}
                          onChange={(e) => handleServiceChange(index, "quantity", e.target.value)}
                          className="form-control form-control-sm"
                        />
                      </div>

                      <div className="col-md-1">
                        <label className="form-label small text-muted mb-0">Disc %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={s.discount_percentage}
                          onChange={(e) =>
                            handleServiceChange(index, "discount_percentage", e.target.value)
                          }
                          className="form-control form-control-sm"
                        />
                      </div>

                      <div className="col-md-2 text-end">
                        <label className="form-label small text-muted mb-0 d-block">Subtotal</label>
                        <strong className="text-success fs-6">₹{total.toLocaleString()}</strong>
                      </div>

                      <div className="col-12">
                        <input
                          type="text"
                          placeholder="Description / notes (optional)"
                          value={s.description}
                          onChange={(e) => handleServiceChange(index, "description", e.target.value)}
                          className="form-control form-control-sm text-muted"
                          style={{ fontSize: "11px" }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                onClick={addServiceRow}
              >
                <i className="fa fa-plus" /> Add Another Service Row
              </button>
            </div>
          </div>

          {/* Section 4: Initial Advance Payment (Optional) */}
          <div className="card border p-3 mb-3 bg-light">
            <div className="form-check form-switch mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="initialPaymentSwitch"
                checked={hasInitialPayment}
                onChange={(e) => setHasInitialPayment(e.target.checked)}
              />
              <label className="form-check-label fw-bold text-dark small" htmlFor="initialPaymentSwitch">
                Record Initial Advance Deposit Now
              </label>
            </div>

            {hasInitialPayment && (
              <div className="row g-2 pt-2 border-top">
                <div className="col-md-4">
                  <label className="form-label small fw-bold text-dark mb-0">Deposit Amount (₹)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 1000"
                    value={paymentData.paid_amount}
                    onChange={(e) =>
                      setPaymentData((prev) => ({ ...prev, paid_amount: e.target.value }))
                    }
                    className="form-control form-control-sm"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold text-dark mb-0">Payment Method</label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) =>
                      setPaymentData((prev) => ({ ...prev, paymentMethod: e.target.value }))
                    }
                    className="form-select form-select-sm"
                  >
                    {GH_PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold text-dark mb-0">Deposit Date</label>
                  <input
                    type="date"
                    value={paymentData.payment_Date}
                    onChange={(e) =>
                      setPaymentData((prev) => ({ ...prev, payment_Date: e.target.value }))
                    }
                    className="form-control form-control-sm"
                  />
                </div>
                <div className="col-12">
                  <input
                    type="text"
                    placeholder="Advance payment notes / transaction ref"
                    value={paymentData.notes}
                    onChange={(e) =>
                      setPaymentData((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    className="form-control form-control-sm text-muted"
                    style={{ fontSize: "11px" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 5: Remarks & Options */}
          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <label className="form-label small fw-bold text-dark mb-1">Stay / Booking Remarks</label>
              <textarea
                rows="2"
                name="notes"
                placeholder="e.g. Patient requested vegetarian meal plan and ground floor room"
                value={formData.notes}
                onChange={handleFormChange}
                className="form-control"
              />
            </div>
            <div className="col-md-4 d-flex align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="saveServicesToMaster"
                  id="saveServicesToMaster"
                  checked={formData.saveServicesToMaster}
                  onChange={handleFormChange}
                />
                <label className="form-check-label small text-muted" htmlFor="saveServicesToMaster">
                  Save new custom services to services catalog for future bookings
                </label>
              </div>
            </div>
          </div>

        </DialogContent>

        {/* Pinned Sticky Footer Summary & Actions */}
        <div
          className="gh-modal-footer"
          style={{
            flexShrink: 0,
            background: "#ffffff",
            borderTop: "1px solid #e2e8f0",
            padding: "14px 22px",
            boxShadow: "0 -4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-4">
              <div>
                <span
                  className="small text-muted d-block"
                  style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Total Billed
                </span>
                <strong className="fs-5 text-dark">₹{totalBilling.toLocaleString()}</strong>
              </div>
              <div>
                <span
                  className="small text-muted d-block"
                  style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Initial Deposit
                </span>
                <span className="badge bg-success fs-6">₹{initialPaid.toLocaleString()}</span>
              </div>
              <div>
                <span
                  className="small text-muted d-block"
                  style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Net Balance Due
                </span>
                <span className={`badge fs-6 ${netDue > 0 ? "bg-danger" : "bg-secondary"}`}>
                  ₹{netDue.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-light btn-sm px-3 border"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
                disabled={loading}
                style={{ backgroundColor: "#0284c7", borderColor: "#0284c7" }}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin" /> Creating...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check" /> Create Service Record
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
